<script setup>
// Barra da lista reformada (prototipo-filtros-lista.html aprovado):
// - a fileira de icones encolhe: funil (painel unico) + status/ordenacao
// - caixa, etiqueta, pasta, prioritarias e grupos moram DENTRO do painel
// - filtro ativo vira chip: ate 2, individuais com X proprio; 3+, colapsa
//   em "N filtros X" (o clique abre o painel, o X limpa tudo)
import { ref, computed } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { getChannelColor } from 'next/icon/provider';

/* eslint-disable vue/no-unused-properties -- pageTitle, activeStatus,
   conversationStats e isListLoading fazem parte do contrato com o ChatList;
   a barra nova nao os exibe mais, mas remover quebraria o chamador. */
const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  inboxes: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  folders: { type: Array, default: () => [] },
  activeInboxId: { type: [String, Number], default: 0 },
  activeLabel: { type: String, default: '' },
  activeFolderId: { type: [String, Number], default: 0 },
  showNonService: { type: Boolean, default: false },
  showGroupsOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'inboxSelect',
  'labelSelect',
  'folderSelect',
  'resetView',
  'toggleNonService',
  'toggleGroups',
]);

const { t } = useI18n();

const showPanel = ref(false);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const inboxAtiva = computed(() =>
  props.inboxes.find(i => i.id === Number(props.activeInboxId))
);
const pastaAtiva = computed(() =>
  props.folders.find(f => f.id === Number(props.activeFolderId))
);

// ── chips dos filtros ativos (hibrido aprovado) ──
const filtrosAtivos = computed(() => {
  const lista = [];
  if (inboxAtiva.value)
    lista.push({
      key: 'inbox',
      texto: inboxAtiva.value.name,
      limpar: () => emit('inboxSelect', null),
    });
  if (props.activeLabel)
    lista.push({
      key: 'label',
      texto: props.activeLabel,
      limpar: () => emit('labelSelect', null),
    });
  if (pastaAtiva.value)
    lista.push({
      key: 'folder',
      texto: pastaAtiva.value.name,
      limpar: () => emit('folderSelect', null),
    });
  if (props.showNonService)
    lista.push({
      key: 'nonservice',
      texto: t('CHAT_LIST.NON_SERVICE_INBOXES'),
      limpar: () => emit('toggleNonService'),
    });
  if (props.showGroupsOnly)
    lista.push({
      key: 'groups',
      texto: t('CHAT_LIST.GROUPS_FILTER'),
      limpar: () => emit('toggleGroups'),
    });
  if (props.hasAppliedFilters)
    lista.push({
      key: 'advanced',
      texto: t('CHAT_LIST.QUICK_FILTERS.ADVANCED_ACTIVE'),
      limpar: () => emit('resetFilters'),
    });
  return lista;
});

const resumoFiltros = computed(() =>
  filtrosAtivos.value.map(f => f.texto).join(' · ')
);

const limparTudo = () => {
  showPanel.value = false;
  emit('resetView');
};

// ── painel ──
const togglePanel = () => {
  showPanel.value = !showPanel.value;
};

const aoEscolherPasta = event => {
  const id = Number(event.target.value);
  emit('folderSelect', id ? props.folders.find(f => f.id === id) : null);
};

const abrirAvancados = () => {
  showPanel.value = false;
  emit('filtersModal');
};

// Sub-listas com busca (modelo do popover de etiquetas do deal): icone de
// canal nas caixas, bolinha de cor nas etiquetas
const listaAberta = ref(null);
const buscaLista = ref('');
const toggleLista = qual => {
  listaAberta.value = listaAberta.value === qual ? null : qual;
  buscaLista.value = '';
};
const casaBusca = texto =>
  !buscaLista.value.trim() ||
  String(texto || '')
    .toLowerCase()
    .includes(buscaLista.value.trim().toLowerCase());
const caixasFiltradas = computed(() =>
  props.inboxes.filter(i => casaBusca(i.name))
);
const etiquetasFiltradas = computed(() =>
  props.labels.filter(l => casaBusca(l.title))
);
const etiquetaAtiva = computed(() =>
  props.labels.find(l => l.title === props.activeLabel)
);
const escolherCaixa = inbox => {
  listaAberta.value = null;
  emit('inboxSelect', inbox);
};
const escolherEtiqueta = label => {
  listaAberta.value = null;
  emit('labelSelect', label);
};
const inboxIcon = inbox =>
  getInboxIconByType(inbox.channel_type, inbox.medium, 'line');
const inboxColorClass = inbox => getChannelColor(inbox.channel_type);
</script>

<template>
  <div class="flex items-center justify-between gap-1 px-3 py-2">
    <!-- Esquerda: SEM filtro ativo a busca mora aqui, na mesma linha dos
         botoes; com filtro, os chips assumem e a busca desce (slot abaixo) -->
    <div class="flex min-w-0 flex-1 items-center gap-1">
      <template v-if="filtrosAtivos.length === 0">
        <slot name="busca" />
      </template>
      <template v-if="filtrosAtivos.length > 0 && filtrosAtivos.length <= 2">
        <span
          v-for="filtro in filtrosAtivos"
          :key="filtro.key"
          class="inline-flex min-w-0 items-center gap-1 rounded-full border border-n-strong bg-white px-2 py-0.5 text-[10.5px] font-semibold text-n-slate-11 dark:bg-n-solid-3"
        >
          <span class="truncate">{{ filtro.texto }}</span>
          <button
            class="text-n-slate-8 hover:text-n-ruby-11"
            @click="filtro.limpar()"
          >
            <span class="i-lucide-x size-2.5 block" />
          </button>
        </span>
      </template>
      <span
        v-else-if="filtrosAtivos.length > 2"
        v-tooltip.right="resumoFiltros"
        class="inline-flex cursor-pointer items-center gap-1 rounded-full border border-n-strong bg-white px-2 py-0.5 text-[10.5px] font-bold text-n-slate-12 dark:bg-n-solid-3"
        @click="togglePanel"
      >
        {{
          $t('CHAT_LIST.QUICK_FILTERS.N_ACTIVE', {
            count: filtrosAtivos.length,
          })
        }}
        <button
          class="text-n-slate-8 hover:text-n-ruby-11"
          @click.stop="limparTudo"
        >
          <span class="i-lucide-x size-2.5 block" />
        </button>
      </span>
    </div>

    <!-- Direita: funil (painel unico) + status/ordenacao + gestao de pastas -->
    <div class="flex flex-shrink-0 items-center gap-0.5">
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>

      <!-- Funil: painel unico de filtros -->
      <div v-on-click-outside="() => (showPanel = false)" class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('CHAT_LIST.QUICK_FILTERS.TITLE')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          :class="{ '!bg-n-brand-50 !text-n-brand': filtrosAtivos.length > 0 }"
          @click="togglePanel"
        />
        <span
          v-if="filtrosAtivos.length"
          class="pointer-events-none absolute -right-1 -top-1 rounded-full bg-n-brand px-1 text-[8.5px] font-bold leading-[13px] text-white"
        >
          {{ filtrosAtivos.length }}
        </span>
        <div
          v-if="!hasActiveFolders"
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />

        <!-- Painel -->
        <div
          v-if="showPanel"
          class="absolute z-50 mt-1 w-[264px] rounded-xl border border-n-strong bg-white p-3 shadow-xl ltr:right-0 rtl:left-0 dark:bg-n-solid-3"
        >
          <div class="mb-2.5 flex items-center">
            <b class="text-[12.5px] text-n-slate-12">
              {{ $t('CHAT_LIST.QUICK_FILTERS.TITLE') }}
            </b>
            <button
              v-if="filtrosAtivos.length"
              class="ml-auto text-[11px] font-bold text-n-brand hover:underline"
              @click="limparTudo"
            >
              {{ $t('CHAT_LIST.QUICK_FILTERS.CLEAR_ALL') }}
            </button>
          </div>

          <div class="mb-2.5">
            <div class="painel-rotulo">
              {{ $t('CHAT_LIST.QUICK_FILTERS.INBOX') }}
            </div>
            <button class="painel-campo" @click="toggleLista('inbox')">
              <template v-if="inboxAtiva">
                <span
                  :class="[inboxIcon(inboxAtiva), inboxColorClass(inboxAtiva)]"
                  class="size-3.5 flex-shrink-0"
                />
                <span class="truncate">{{ inboxAtiva.name }}</span>
              </template>
              <span v-else class="text-n-slate-10">
                {{ $t('CHAT_LIST.QUICK_FILTERS.ALL_INBOXES') }}
              </span>
              <span
                class="i-lucide-chevron-down ml-auto size-3 text-n-slate-9"
              />
            </button>
            <div v-if="listaAberta === 'inbox'" class="painel-lista">
              <input
                v-model="buscaLista"
                type="text"
                class="!mb-1 !h-7 w-full rounded-lg !text-xs"
                :placeholder="$t('CHAT_LIST.QUICK_FILTERS.SEARCH')"
              />
              <button class="painel-opcao" @click="escolherCaixa(null)">
                <span class="i-lucide-layers size-3.5 text-n-slate-9" />
                {{ $t('CHAT_LIST.QUICK_FILTERS.ALL_INBOXES') }}
              </button>
              <button
                v-for="inbox in caixasFiltradas"
                :key="inbox.id"
                class="painel-opcao"
                :class="{ ativa: inboxAtiva && inboxAtiva.id === inbox.id }"
                @click="escolherCaixa(inbox)"
              >
                <span
                  :class="[inboxIcon(inbox), inboxColorClass(inbox)]"
                  class="size-3.5 flex-shrink-0"
                />
                <span class="truncate">{{ inbox.name }}</span>
              </button>
            </div>
          </div>

          <div class="mb-2.5">
            <div class="painel-rotulo">
              {{ $t('CHAT_LIST.QUICK_FILTERS.LABEL') }}
            </div>
            <button class="painel-campo" @click="toggleLista('label')">
              <template v-if="etiquetaAtiva">
                <span
                  class="size-2.5 flex-shrink-0 rounded-full"
                  :style="{ background: etiquetaAtiva.color }"
                />
                <span class="truncate">{{ etiquetaAtiva.title }}</span>
              </template>
              <span v-else class="text-n-slate-10">
                {{ $t('CHAT_LIST.QUICK_FILTERS.ANY_LABEL') }}
              </span>
              <span
                class="i-lucide-chevron-down ml-auto size-3 text-n-slate-9"
              />
            </button>
            <div v-if="listaAberta === 'label'" class="painel-lista">
              <input
                v-model="buscaLista"
                type="text"
                class="!mb-1 !h-7 w-full rounded-lg !text-xs"
                :placeholder="$t('CHAT_LIST.QUICK_FILTERS.SEARCH')"
              />
              <button class="painel-opcao" @click="escolherEtiqueta(null)">
                <span class="i-lucide-layers size-3.5 text-n-slate-9" />
                {{ $t('CHAT_LIST.QUICK_FILTERS.ANY_LABEL') }}
              </button>
              <button
                v-for="label in etiquetasFiltradas"
                :key="label.id"
                class="painel-opcao"
                :class="{ ativa: activeLabel === label.title }"
                @click="escolherEtiqueta(label)"
              >
                <span
                  class="size-2.5 flex-shrink-0 rounded-full"
                  :style="{ background: label.color }"
                />
                <span class="truncate">{{ label.title }}</span>
              </button>
            </div>
          </div>

          <div v-if="folders.length" class="mb-2.5">
            <div class="painel-rotulo">
              {{ $t('CHAT_LIST.QUICK_FILTERS.FOLDER') }}
            </div>
            <select
              class="!mb-0 !h-8 w-full rounded-lg !text-xs"
              :value="pastaAtiva ? pastaAtiva.id : 0"
              @change="aoEscolherPasta"
            >
              <option :value="0">
                {{ $t('CHAT_LIST.QUICK_FILTERS.NO_FOLDER') }}
              </option>
              <option
                v-for="folder in folders"
                :key="folder.id"
                :value="folder.id"
              >
                {{ folder.name }}
              </option>
            </select>
          </div>

          <div class="mb-2.5">
            <div class="painel-rotulo">
              {{ $t('CHAT_LIST.QUICK_FILTERS.SHOW') }}
            </div>
            <button class="painel-toggle" @click="emit('toggleNonService')">
              {{ $t('CHAT_LIST.NON_SERVICE_INBOXES') }}
              <span class="painel-switch" :class="{ ligado: showNonService }" />
            </button>
            <button class="painel-toggle" @click="emit('toggleGroups')">
              {{ $t('CHAT_LIST.GROUPS_FILTER') }}
              <span class="painel-switch" :class="{ ligado: showGroupsOnly }" />
            </button>
          </div>

          <button
            class="mt-1 flex w-full items-center gap-1.5 border-t border-dashed border-n-weak pt-2.5 text-[11.5px] text-n-slate-10 hover:text-n-slate-12"
            @click="abrirAvancados"
          >
            <span class="i-lucide-sliders-horizontal size-3" />
            {{ $t('CHAT_LIST.QUICK_FILTERS.ADVANCED') }}
          </button>
        </div>
      </div>

      <!-- Status + ordenacao (controle existente) -->
      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />
    </div>
  </div>
  <!-- Filtro ativo: a busca desce pra linha propria -->
  <div v-if="filtrosAtivos.length > 0 && $slots.busca" class="px-3 pb-1.5">
    <slot name="busca" />
  </div>
</template>

<style scoped>
.painel-campo {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgb(var(--n-weak));
  border-radius: 8px;
  padding: 6px 9px;
  font-size: 12px;
  color: rgb(var(--slate-12));
  background: transparent;
  text-align: left;
}

.painel-lista {
  margin-top: 4px;
  border: 1px solid rgb(var(--n-weak));
  border-radius: 10px;
  padding: 6px;
  max-height: 200px;
  overflow-y: auto;
}

.painel-opcao {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 7px;
  border-radius: 7px;
  font-size: 12px;
  color: rgb(var(--slate-11));
  text-align: left;
}

.painel-opcao:hover {
  background: rgb(var(--n-alpha-1));
}

.painel-opcao.ativa {
  background: rgb(var(--brand-50));
  color: rgb(var(--brand-primary));
  font-weight: 600;
}

.painel-rotulo {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgb(var(--slate-10));
  margin-bottom: 4px;
}

.painel-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
  font-size: 12px;
  color: rgb(var(--slate-11));
  text-align: left;
}

.painel-switch {
  width: 30px;
  height: 17px;
  border-radius: 99px;
  background: rgb(var(--slate-6));
  position: relative;
  flex-shrink: 0;
  transition: background 0.15s;
}

.painel-switch::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 2px;
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.15s;
}

.painel-switch.ligado {
  background: rgb(var(--brand-primary));
}

.painel-switch.ligado::after {
  left: 15px;
}
</style>
